import { useEffect, useState } from 'react';
import { Cell, Divider, DeprecatedIcon, Rating, Spacer, Text } from '@vinted/web-ui';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import useBreakpoint from 'hooks/useBreakpoint';
import useTranslate from 'hooks/useTranslate';
import { getIsFeatureSwitchEnabled } from 'state/feature-switches/selectors';
import { UiState } from 'constants/ui';
import { getFeedbackSummary } from 'data/api';
import { transformFeedbackSummaryDto } from 'data/transformers/feedback-summary';
import ContentLoader from 'components/ContentLoader/ContentLoader';
import ErrorState from 'components/ErrorState/ErrorState';
import FaqEntryUrl from 'components/FaqEntryUrl';
import { FaqEntryType } from 'constants/faq-entry';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";

const FeedbackSummary = _ref => {
  let {
    userId
  } = _ref;
  const breakpoints = useBreakpoint();
  const translate = useTranslate('user.feedback.summary');
  const isTransparentFeedbackEnabled = useSelector(getIsFeatureSwitchEnabled('web_transparent_autofeedback'));
  const {
    0: summary,
    1: setSummary
  } = useState(null);
  const {
    0: uiState,
    1: setUiState
  } = useState(UiState.Idle);
  useEffect(() => {
    const fetchFeedbackSummary = async () => {
      setUiState(UiState.Pending);
      const response = await getFeedbackSummary(userId);

      if ('errors' in response) {
        setUiState(UiState.Failure);
        return;
      }

      const transformedSummary = response.user_feedback_summary ? transformFeedbackSummaryDto(response.user_feedback_summary) : null;
      setSummary(transformedSummary);
      setUiState(UiState.Success);
    };

    if (!isTransparentFeedbackEnabled) return;
    fetchFeedbackSummary();
  }, [isTransparentFeedbackEnabled, userId]);

  const renderStarWithRatingSection = rating => {
    const renderRatingWithStar = () => /*#__PURE__*/_jsxs("div", {
      className: "u-flexbox",
      children: [/*#__PURE__*/_jsx(Text, {
        text: rating
      }), /*#__PURE__*/_jsx(Spacer, {
        orientation: Spacer.Orientation.Vertical
      }), rating > 0 && /*#__PURE__*/_jsx("div", {
        className: "u-flexbox u-align-items-center",
        children: /*#__PURE__*/_jsx(DeprecatedIcon, {
          name: "star-filled",
          color: DeprecatedIcon.Color.Expose,
          size: DeprecatedIcon.Size.Small,
          testId: "rating-icon"
        })
      })]
    });

    return /*#__PURE__*/_jsx(Cell, {
      styling: Cell.Styling.Narrow,
      prefix: !breakpoints.portables && renderRatingWithStar(),
      suffix: breakpoints.portables && renderRatingWithStar()
    });
  };

  const renderRatingCountSeparationStatsSection = title => {
    const layoutClassNames = classNames('u-flexbox', {
      'u-nowrap': !breakpoints.portables
    });
    return /*#__PURE__*/_jsx(Cell, {
      styling: Cell.Styling.Narrow,
      children: /*#__PURE__*/_jsx("div", {
        className: layoutClassNames,
        children: /*#__PURE__*/_jsx(Text, {
          bold: true,
          text: title
        })
      })
    });
  };

  const renderRatingCountSeparationStats = () => {
    if (!summary) return null;
    return /*#__PURE__*/_jsxs("div", {
      className: "u-flexbox u-flex-direction-column",
      children: [renderRatingCountSeparationStatsSection(translate('manual_feedback', {
        count: summary.userFeedbackCount
      })), renderRatingCountSeparationStatsSection(translate('auto_feedback', {
        count: summary.systemFeedbackCount
      }))]
    });
  };

  const renderTotalRatingWithSeparationStats = () => {
    if (!summary) return null;
    const flexDirectionClass = breakpoints.portables ? 'u-flex-direction-column' : 'u-flex-direction-row';
    const ratingClassNames = classNames('u-flexbox u-flex-direction-column', {
      'u-align-items-center': breakpoints.portables
    });
    return /*#__PURE__*/_jsx(Cell, {
      children: /*#__PURE__*/_jsxs("div", {
        className: "u-flexbox u-align-items-center ".concat(flexDirectionClass),
        children: [/*#__PURE__*/_jsx("p", {
          className: "rating-score-huge-text",
          children: summary.feedbackRating
        }), /*#__PURE__*/_jsx(Spacer, {
          orientation: breakpoints.portables ? Spacer.Orientation.Horizontal : Spacer.Orientation.Vertical,
          size: Spacer.Size.Large
        }), /*#__PURE__*/_jsxs("div", {
          className: ratingClassNames,
          children: [/*#__PURE__*/_jsx(Rating, {
            value: summary.feedbackRating
          }), !breakpoints.portables && /*#__PURE__*/_jsx(Spacer, {
            size: Spacer.Size.Large
          }), /*#__PURE__*/_jsx(Text, {
            text: "(".concat(summary.feedbackCount, ")")
          })]
        }), /*#__PURE__*/_jsx(Spacer, {
          orientation: Spacer.Orientation.Vertical,
          size: Spacer.Size.Large
        }), !breakpoints.portables && renderRatingCountSeparationStats()]
      })
    });
  };

  const renderInformationLink = () => /*#__PURE__*/_jsx("div", {
    className: "u-flexbox u-justify-content-flex-end u-nowrap",
    children: /*#__PURE__*/_jsx(Cell, {
      styling: breakpoints.portables ? Cell.Styling.Narrow : Cell.Styling.Wide,
      prefix: /*#__PURE__*/_jsx(FaqEntryUrl, {
        type: FaqEntryType.LeavingFeedbackForBuyer,
        render: url => /*#__PURE__*/_jsx("a", {
          href: url,
          target: "_blank",
          rel: "noreferrer",
          children: translate('learn_more')
        })
      })
    })
  });

  const renderRatingCountSeparationStars = () => {
    if (!summary) return null;
    const manualFeedbackStarSection = summary.userFeedbackCount > 0 ? renderStarWithRatingSection(summary.userFeedbackRating) : /*#__PURE__*/_jsx(Cell, {
      children: /*#__PURE__*/_jsx(Spacer, {})
    });
    const autoFeedbackStarSection = summary.systemFeedbackCount > 0 ? renderStarWithRatingSection(summary.systemFeedbackRating) : /*#__PURE__*/_jsx(Cell, {
      children: /*#__PURE__*/_jsx(Spacer, {})
    });
    return /*#__PURE__*/_jsx(Cell, {
      children: /*#__PURE__*/_jsxs("div", {
        className: "u-flexbox u-flex-direction-column",
        children: [/*#__PURE__*/_jsxs("div", {
          className: "u-flexbox",
          children: [breakpoints.portables && renderRatingCountSeparationStatsSection(translate('manual_feedback', {
            count: summary.userFeedbackCount
          })), manualFeedbackStarSection]
        }), /*#__PURE__*/_jsxs("div", {
          className: "u-flexbox",
          children: [breakpoints.portables && renderRatingCountSeparationStatsSection(translate('auto_feedback', {
            count: summary.systemFeedbackCount
          })), autoFeedbackStarSection]
        }), breakpoints.portables && renderInformationLink()]
      })
    });
  };

  const layoutClassNames = classNames('u-flexbox', {
    'u-flex-direction-column': breakpoints.portables,
    'u-flex-direction-row': !breakpoints.portables
  });
  if (uiState === UiState.Pending) return /*#__PURE__*/_jsx(ContentLoader, {});
  if (uiState === UiState.Failure) return /*#__PURE__*/_jsx(ErrorState, {});
  if (!isTransparentFeedbackEnabled || !summary) return null;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs("div", {
      className: layoutClassNames,
      children: [breakpoints.portables && /*#__PURE__*/_jsx(Spacer, {
        size: Spacer.Size.Large
      }), renderTotalRatingWithSeparationStats(), renderRatingCountSeparationStars(), !breakpoints.portables && renderInformationLink()]
    }), /*#__PURE__*/_jsx(Divider, {})]
  });
};

export default FeedbackSummary;